import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStamps } from '../hooks/useStamps';
import { useHaptic } from '../hooks/useHaptic';
import DocumentViewer from '../components/DocumentViewer';
import StampSelector from '../components/StampSelector';
import SideSheet from '../components/SideSheet';
import { documentsAPI, pendingDocumentsAPI } from '../services/api';
import { FileCheck, Loader2, Trash2, Stamp } from 'lucide-react';
import './SignPage.css';

function SignPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { document: uploadedDoc, pendingDocument } = location.state || {};
  const { stamps, refetch: refetchStamps } = useStamps();
  const haptic = useHaptic();
  
  const [selectedStamp, setSelectedStamp] = useState(null);
  const [placedStamps, setPlacedStamps] = useState([]);
  const [signing, setSigning] = useState(false);
  const [documentData, setDocumentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [documentDimensions, setDocumentDimensions] = useState(null); // Размеры документа
  const [isSideSheetOpen, setIsSideSheetOpen] = useState(false); // Состояние side sheet
  const [isStampEditing, setIsStampEditing] = useState(false); // Состояние редактирования печати

  // Определяем, с каким типом документа работаем
  const isPending = pendingDocument?.isPending;
  const currentDoc = isPending ? pendingDocument : uploadedDoc;

  useEffect(() => {
    if (!currentDoc) {
      navigate('/');
      return;
    }

    // Если это pending документ, загружаем его данные
    if (isPending) {
      fetchPendingDocument();
    }
  }, [currentDoc, navigate, isPending]);

  const fetchPendingDocument = async () => {
    try {
      setLoading(true);
      const response = await pendingDocumentsAPI.getById(pendingDocument.id);
      setDocumentData(response.data.document);
    } catch (error) {
      console.error('Ошибка загрузки документа:', error);
      alert('Ошибка при загрузке документа');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleStampPlace = (stampData) => {
    haptic.stampPlace();
    setPlacedStamps([...placedStamps, stampData]);
  };

  const handleStampUpdate = (updatedStamp) => {
    setPlacedStamps(placedStamps.map(s => 
      s.id === updatedStamp.id ? updatedStamp : s
    ));
  };

  const handleRemoveStamp = (id) => {
    haptic.delete();
    setPlacedStamps(placedStamps.filter(s => s.id !== id));
  };

  const handleSign = async () => {
    if (placedStamps.length === 0) {
      haptic.warning();
      alert('Разместите хотя бы одну печать или подпись на документе');
      return;
    }

    console.log('🔍 Проверка documentDimensions:', documentDimensions);

    if (!documentDimensions) {
      console.error('❌ documentDimensions отсутствует!');
      haptic.error();
      alert('Подождите загрузки документа');
      return;
    }

    try {
      setSigning(true);
      haptic.buttonPressImportant();
      
      // Используем правильный ID документа в зависимости от типа
      const docId = isPending ? pendingDocument.id : uploadedDoc.id;
      
      const canvasWidth = documentDimensions.canvas.width;
      const canvasHeight = documentDimensions.canvas.height;
      
      console.log('📐 Размеры canvas:', { canvasWidth, canvasHeight });
      console.log('🖼️ Печати ДО пересчета:', placedStamps);
      
      // Преобразуем координаты в проценты относительно canvas
      const stampsData = placedStamps.map(stamp => {
        const percentX = (stamp.x / canvasWidth) * 100;
        const percentY = (stamp.y / canvasHeight) * 100;
        const percentWidth = (stamp.width / canvasWidth) * 100;
        const percentHeight = (stamp.height / canvasHeight) * 100;
        
        const stampData = {
          stampId: stamp.stampId,
          // Отправляем проценты вместо абсолютных координат
          percentX: percentX,
          percentY: percentY,
          percentWidth: percentWidth,
          percentHeight: percentHeight,
          rotation: stamp.rotation || 0,
          pageNumber: stamp.pageNumber || 0,
        };
        
        console.log('🔄 Преобразование в проценты:', {
          original: { x: stamp.x, y: stamp.y, width: stamp.width, height: stamp.height },
          percent: { 
            x: percentX.toFixed(2) + '%', 
            y: percentY.toFixed(2) + '%', 
            width: percentWidth.toFixed(2) + '%', 
            height: percentHeight.toFixed(2) + '%' 
          }
        });
        
        return stampData;
      });

      console.log('📤 Отправка на подписание:', {
        docId,
        isPending,
        stampsCount: stampsData.length,
        stampsData
      });
      
      // Передаем флаг isPending в API
      await documentsAPI.sign(docId, stampsData, isPending);
      
      haptic.documentSign();
      alert('✅ Документ успешно подписан!');
      navigate('/history');
    } catch (error) {
      console.error('Ошибка подписания:', error);
      console.error('Детали:', error.response?.data);
      haptic.error();
      
      if (error.response?.status === 403) {
        alert('Достигнут лимит документов. Оформите подписку для продолжения.');
        navigate('/upgrade');
      } else {
        alert('Ошибка при подписании документа. Попробуйте еще раз.');
      }
    } finally {
      setSigning(false);
    }
  };

  if (!currentDoc) {
    return null;
  }

  if (loading) {
    return (
      <div className="sign-page-wrapper">
        <div className="sign-page">
          <div className="sign-content" style={{ minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader2 className="loader-icon" />
            <p>Загрузка документа...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sign-page-wrapper">
      <div className="sign-page">
        <div className="document-title-section animate-fade-in">
          <h2 className="document-name">{currentDoc.fileName}</h2>
        </div>

        <div className="sign-content animate-slide-up animate-delay-100">
          <DocumentViewer
            document={uploadedDoc}
            pendingDocument={pendingDocument}
            selectedStamp={selectedStamp}
            placedStamps={placedStamps}
            onStampPlace={handleStampPlace}
            onStampUpdate={handleStampUpdate}
            onRemoveStamp={handleRemoveStamp}
            onClearSelection={() => setSelectedStamp(null)}
            onDocumentDimensionsChange={setDocumentDimensions}
            onStampEditingChange={setIsStampEditing}
          />
        </div>

        {/* Плавающая кнопка для открытия галереи - скрываем при редактировании */}
        {!isStampEditing && (
          <button 
            className="fab-button animate-scale-in animate-delay-300"
            onClick={() => {
              haptic.buttonPress();
              setIsSideSheetOpen(true);
            }}
            aria-label="Открыть галерею печатей"
          >
            <Stamp size={24} />
            {placedStamps.length > 0 && (
              <span className="fab-badge">{placedStamps.length}</span>
            )}
          </button>
        )}

        {/* Side Sheet с галереей печатей */}
        <SideSheet 
          isOpen={isSideSheetOpen}
          onClose={() => {
            haptic.modalClose();
            setIsSideSheetOpen(false);
          }}
          title="Печати и подписи"
        >
          <StampSelector
            stamps={stamps}
            selectedStamp={selectedStamp}
            onSelectStamp={(stamp) => {
              haptic.itemSelect();
              setSelectedStamp(stamp);
              setIsSideSheetOpen(false); // Автоматически закрываем панель после выбора
            }}
            onStampsUpdate={refetchStamps}
          />

          <div className="placed-stamps-list">
            <h3>Размещенные печати ({placedStamps.length})</h3>
            {placedStamps.length === 0 ? (
              <p className="empty-list">Нет размещенных печатей</p>
            ) : (
              placedStamps.map((stamp) => (
                <div key={stamp.id} className="placed-stamp-item">
                  <span>Печать #{stamp.id}</span>
                  <button 
                    className="remove-stamp-button"
                    onClick={() => handleRemoveStamp(stamp.id)}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))
            )}
          </div>
        </SideSheet>

        <div className="fixed-button-container">
          <button
            className="button sign-button-fixed"
            onClick={handleSign}
            disabled={signing || placedStamps.length === 0}
          >
            {signing ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                <span>Подписание...</span>
              </>
            ) : (
              <>
                <FileCheck size={20} />
                <span>Подписать документ</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SignPage;

